import { useEffect, useState } from 'react';
import { FileDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useToast } from '@/components/Toast';
import { DataGrid, Column } from '@/components/DataGrid';

type Row = Record<string, unknown>;

// Column names + order, verbatim from the Power Apps report ClientEngagementOnboardingReport
// (Reports.tb_ClientImport / tb_EngagementImport) — the Maconomy onboarding import format.
const CLIENT_COLS = [
  'CUSTOMER:FORMAT', 'InsertBelowParentInHierarchy', 'LinkingRuleName', 'ParentCustomer',
  'CustomerNumber', 'PaymentCustomer', 'Name1', 'Attention', 'ContactPerson', 'Name2', 'Name3',
  'PostalDistrict', 'Country', 'ZipCode', 'Telephone', 'Telefax', 'ElectronicMailAddress',
  'CustomerRemark12', 'Telex', 'AccountManagerNumber', 'SPECIFICATION1NAME', 'SelectedOption10',
  'SelectedOption5', 'FiscalYearEndMonth', 'VATNumber', 'Remark', 'CustomerPopup3',
  'CustomerRemark13', 'CustomerRemark14', 'EmployeeNumber7',
];
const ENG_COLS = [
  'JOBHEADER:FORMAT', 'JobNumber', 'CustomerNumber', 'JobName', 'TemplateJobNumber', 'TheYear',
  'EmployeeNumber6', 'AccountManagerNumber', 'SalesPersonNumber', 'ProjectManagerNumber',
  'EmployeeNumber1', 'ContractedBillingPriceForTimeCurrency', 'Specification2Name',
  'Specification3Name', 'ELSigner', 'ELSignerEmail', 'SelectedCustomerJobOption2',
  'SelectedCustomerJobOption1', 'SelectedCustomerJobOption3', 'Popup3', 'Popup4', 'Popup5',
  'Telex', 'ElectronicMailAddress',
];

// Header labels that differ from the data key, to match the Maconomy import template
// (the EL-signer name/email columns are labelled "Text3"/"Text4" there). Kept in sync
// with IMPORT_HEADER_LABELS in the API's requests.service.ts.
const HEADER_LABELS: Record<string, string> = { ELSigner: 'Text3', ELSignerEmail: 'Text4' };
const toCols = (names: string[]): Column<Row>[] =>
  names.map((c) => ({ key: c, header: HEADER_LABELS[c] ?? c }));
const clientCols = toCols(CLIENT_COLS);
const engCols = toCols(ENG_COLS);

/**
 * Downloaded Pending Requests — replaces the Power Apps embedded Power BI report
 * ClientEngagementOnboardingReport. Shows the Maconomy onboarding import data
 * (Reports.tb_ClientImport / tb_EngagementImport) and exports it to Excel — the
 * file BRC feeds into Maconomy.
 */
export function DownloadPending() {
  const notify = useToast();
  // Reports.tb_ClientImport / tb_EngagementImport are only rebuilt as a side-effect of
  // submitting or deleting a request, so arriving here directly would show a stale
  // snapshot (e.g. old counts, or none at all). Regenerate them from the live pending
  // queue on entry — this mirrors the Power Apps "Download Pending Requests" flow (which
  // runs the same import and logs a "Download pending ... request" activity) — then read.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await Promise.all([
          api.post('/imports/clients', {}),
          api.post('/imports/engagements', {}),
        ]);
      } catch {
        notify('error', 'Could not regenerate the import — showing the last generated data.');
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clients = useQuery({
    queryKey: ['client-import-report'],
    queryFn: () => api.get<Row[]>('/reports/client-import'),
    enabled: ready,
  });
  const engagements = useQuery({
    queryKey: ['engagement-import-report'],
    queryFn: () => api.get<Row[]>('/reports/engagement-import'),
    enabled: ready,
  });
  const dl = async (path: string, file: string) => {
    try {
      await api.download(path, file);
    } catch {
      notify('error', 'Export failed');
    }
  };

  return (
    <div className="space-y-8 p-6">
      <div>
        <h2 className="text-lg font-semibold text-primary">Downloaded Pending Requests</h2>
        <p className="text-sm text-muted-foreground">
          Maconomy onboarding import for the pending client and engagement requests. Use Export to
          download the file to feed into Maconomy.
        </p>
      </div>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold text-primary">
            Pending Client Requests Report{' '}
            <span className="text-sm font-normal text-muted-foreground">({(clients.data ?? []).length})</span>
          </h3>
          <button className="btn-secondary inline-flex items-center gap-1"
            onClick={() => dl('/reports/client-import.xlsx', 'client-onboarding-import.xlsx')}>
            <FileDown className="h-4 w-4" /> Export
          </button>
        </div>
        <DataGrid columns={clientCols} rows={clients.data ?? []} loading={!ready || clients.isFetching}
          heightClass="max-h-[65vh]"
          empty="No client rows in the current import." />
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold text-primary">
            Pending Engagement Requests Report{' '}
            <span className="text-sm font-normal text-muted-foreground">({(engagements.data ?? []).length})</span>
          </h3>
          <button className="btn-secondary inline-flex items-center gap-1"
            onClick={() => dl('/reports/engagement-import.xlsx', 'engagement-onboarding-import.xlsx')}>
            <FileDown className="h-4 w-4" /> Export
          </button>
        </div>
        <DataGrid columns={engCols} rows={engagements.data ?? []} loading={!ready || engagements.isFetching}
          heightClass="max-h-[65vh]"
          empty="No engagement rows in the current import." />
      </section>
    </div>
  );
}
